import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CabeceraPagina from "../../components/CabeceraPagina.jsx";
import { Badge, EstadoVacio, InputBusqueda, coincideBusqueda, euros } from "../../components/ui.jsx";
import { ESTADOS_OS, tonoEstado } from "./datos.js";
import RecepcionRapidaServicio from "./RecepcionRapidaServicio.jsx";
import FormOrdenServicio from "./FormOrdenServicio.jsx";
import MenuImprimirOrdenServicio from "../../components/MenuImprimirOrdenServicio.jsx";
import ModalRecepcionServicio from "./ModalRecepcionServicio.jsx";

// Icono de cámara para la recepción digital (fotos del estado + firma).
function IconCamara() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES") : "—");

// Nº del presupuesto vinculado a la orden (píldora violeta).
function BadgePresupuesto({ numero }) {
  if (!numero) return null;
  return (
    <span
      title={`Nace del presupuesto ${numero}`}
      className="inline-block rounded-full border bg-violet-100 text-violet-700 border-violet-200 text-[0.625rem] font-semibold px-1.5 py-px ml-1.5 align-middle"
    >
      {numero}
    </span>
  );
}

// Servicio a domicilio (el técnico se desplaza): píldora teal.
function BadgeDomicilio({ tipoServicio }) {
  if (tipoServicio !== "domicilio") return null;
  return (
    <span
      title="Servicio a domicilio: el técnico se desplaza"
      className="inline-block rounded-full border bg-teal-100 text-teal-700 border-teal-200 text-[0.625rem] font-semibold px-1.5 py-px ml-1.5 align-middle"
    >
      Domicilio
    </span>
  );
}

// Desplegable de estado con aspecto de pill coloreada (referencia).
const CLASES_PILL_ESTADO = {
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  cyan: "bg-sky-100 text-sky-700 border-sky-200",
  green: "bg-emerald-100 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};

// Vista kanban (referencia RO App /orders/board): una columna por estado,
// tarjetas arrastrables que cambian el estado de la orden al soltarse.
function TableroKanban({ ordenes, onMover, onEditar, onFacturar, onRecepcion, onBorrar }) {
  // Auto-scroll de la ventana mientras se arrastra: sin esto, las tarjetas
  // que están abajo del todo no se pueden llevar a las columnas de arriba.
  useEffect(() => {
    const MARGEN = 90;
    const VELOCIDAD = 20;
    let raf = null;
    let dy = 0;
    const paso = () => {
      window.scrollBy(0, dy);
      raf = dy !== 0 ? requestAnimationFrame(paso) : null;
    };
    const alArrastrar = (e) => {
      if (e.clientY > 0 && e.clientY < MARGEN) dy = -VELOCIDAD;
      else if (e.clientY > window.innerHeight - MARGEN) dy = VELOCIDAD;
      else dy = 0;
      if (dy !== 0 && raf === null) raf = requestAnimationFrame(paso);
    };
    const alSoltar = () => { dy = 0; };
    window.addEventListener("dragover", alArrastrar);
    window.addEventListener("drop", alSoltar);
    window.addEventListener("dragend", alSoltar);
    return () => {
      window.removeEventListener("dragover", alArrastrar);
      window.removeEventListener("drop", alSoltar);
      window.removeEventListener("dragend", alSoltar);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
      {ESTADOS_OS.map((col) => {
        const enColumna = ordenes.filter((o) => o.estado === col.clave);
        return (
          <div
            key={col.clave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/orden-id");
              const o = ordenes.find((x) => x._id === id);
              if (o && o.estado !== col.clave) onMover(o, col.clave);
            }}
            className="rounded-xl bg-slate-100/80 border border-slate-200/70 p-2.5 min-h-[220px]"
          >
            <div className="flex items-center justify-between px-1 pb-2.5">
              <span
                className={`inline-block rounded-full border text-[0.6875rem] font-semibold px-3 py-1 ${
                  CLASES_PILL_ESTADO[col.tono] ?? CLASES_PILL_ESTADO.slate
                }`}
              >
                {col.nombre}
              </span>
              <span className="text-xs font-medium text-slate-400 num">{enColumna.length}</span>
            </div>
            <div className="space-y-2">
              {enColumna.map((o) => (
                <div
                  key={o._id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/orden-id", o._id)}
                  className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 cursor-grab active:cursor-grabbing hover:border-accent/60 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-slate-800 num">
                      {o.numero}
                      <BadgePresupuesto numero={o.presupuestoNumero} />
                      <BadgeDomicilio tipoServicio={o.tipoServicio} />
                    </p>
                    <p className="num text-sm font-semibold text-slate-700">{euros(o.total)}</p>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5 truncate">{o.aparatoDescripcion ?? "—"}</p>
                  <p className="text-xs text-slate-500 truncate">{o.clienteNombre ?? "—"}</p>
                  {o.averia && (
                    <p className="text-[0.6875rem] text-slate-400 mt-1 truncate">{o.averia}</p>
                  )}
                  {/* Cambio de estado sin arrastrar */}
                  <select
                    value={o.estado}
                    onChange={(e) => e.target.value !== o.estado && onMover(o, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    draggable={false}
                    className={`mt-2 w-full rounded-full border text-[0.6875rem] font-semibold px-2 py-1 cursor-pointer ${
                      CLASES_PILL_ESTADO[tonoEstado(o.estado)] ?? CLASES_PILL_ESTADO.slate
                    }`}
                    title="Cambiar de estado sin arrastrar"
                  >
                    {ESTADOS_OS.map((col) => (
                      <option key={col.clave} value={col.clave}>{col.nombre}</option>
                    ))}
                  </select>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[0.6875rem] text-slate-400 num">{fecha(o.fechaEntrada)}</span>
                    <span className="flex items-center gap-2">
                      <MenuImprimirOrdenServicio orden={o} pequeno />
                      <button
                        onClick={() => onRecepcion(o)}
                        title="Recepción digital (fotos + firma)"
                        className={`inline-flex items-center transition-colors ${
                          o.recepcionDigital?.firma?.fecha
                            ? "text-emerald-500 hover:text-emerald-600"
                            : "text-slate-400 hover:text-accent"
                        }`}
                      >
                        <IconCamara />
                      </button>
                      <button
                        onClick={() => onEditar(o)}
                        className="text-[0.6875rem] text-accent hover:underline"
                      >
                        Editar
                      </button>
                      {!o.factura && ["finalizado", "entregado"].includes(o.estado) && (
                        <button
                          onClick={() => onFacturar(o)}
                          className="text-[0.6875rem] text-amber-600 hover:underline"
                        >
                          Facturar
                        </button>
                      )}
                      <button
                        onClick={() => onBorrar(o)}
                        title="Borrar orden"
                        className="text-[0.6875rem] text-rose-400 hover:text-rose-600 hover:underline"
                      >
                        Borrar
                      </button>
                    </span>
                  </div>
                </div>
              ))}
              {enColumna.length === 0 && (
                <p className="text-[0.6875rem] text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                  Arrastra aquí
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ServicioOrdenesPage() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [recepcion, setRecepcion] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  // undefined = cerrado · null = orden nueva · objeto = edición
  const [ordenForm, setOrdenForm] = useState(undefined);
  const [recepcionOS, setRecepcionOS] = useState(undefined); // orden en recepción digital
  const [recepcionNueva, setRecepcionNueva] = useState(false); // recién creada (flujo recepción rápida)
  const [vista, setVista] = useState("tablero"); // "tablero" (kanban) | "lista"

  async function cargar() {
    try {
      const r = await fetch("/api/servicio/ordenes");
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "Error al cargar");
      setLista(datos);
    } catch (e) {
      setError(e.message);
      setLista([]);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function cambiarEstado(o, estado) {
    const r = await fetch(`/api/servicio/ordenes/${o._id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
    const datos = await r.json();
    if (r.ok) cargar();
    else alert(datos.error || "No se pudo cambiar el estado");
  }

  async function borrar(o) {
    const texto = o.factura
      ? `La orden ${o.numero} tiene una factura asociada (${o.numeroFactura ?? "borrador"}).\n\n` +
        `La factura NO se borra: es un documento legal y queda registrada.\n` +
        `Se borra solo la orden, con sus fotos y su historial.\n\n` +
        `¿Borrar la orden?`
      : `¿Borrar la orden ${o.numero}? Se borran también sus fotos y su historial.`;
    if (!window.confirm(texto)) return;
    const r = await fetch(`/api/servicio/ordenes/${o._id}`, { method: "DELETE" });
    if (r.ok) cargar();
    else alert((await r.json()).error || "No se pudo borrar");
  }

  async function facturar(o) {
    if (!window.confirm(`¿Generar la factura (borrador) de ${o.numero} por ${euros(o.total)}?`)) return;
    const r = await fetch(`/api/servicio/ordenes/${o._id}/facturar`, { method: "POST" });
    const datos = await r.json();
    if (r.ok) {
      if (window.confirm("Factura creada en Ventas como borrador. ¿Quieres ir a Ventas para validarla y emitirla?")) {
        window.location.href = "/ventas";
      }
      cargar();
    } else alert(datos.error || "No se pudo facturar");
  }

  const q = busqueda.trim();
  const filtrada = (lista ?? []).filter((o) =>
    coincideBusqueda(q, o.numero, o.aparatoDescripcion, o.clienteNombre, o.aparato?.numeroSerie)
  );

  return (
    <>
      <CabeceraPagina titulo="Órdenes de servicio" descripcion="Reparaciones de informática y electrónica, en tienda y a domicilio.">
        <div className="inline-flex rounded-lg overflow-hidden border border-slate-300 bg-white text-[0.78125rem] font-semibold mr-2">
          <button
            onClick={() => setVista("tablero")}
            className={`px-3.5 py-2 transition-colors ${vista === "tablero" ? "seg-activo" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}
          >
            Tablero
          </button>
          <button
            onClick={() => setVista("lista")}
            className={`px-3.5 py-2 transition-colors ${vista === "lista" ? "seg-activo" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}
          >
            Lista
          </button>
        </div>
        <button onClick={() => setOrdenForm(null)} className="btn-primary mr-2">
          + Nueva orden
        </button>
        <button
          onClick={() => setRecepcion(true)}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
          </svg>
          Recepción rápida
        </button>
      </CabeceraPagina>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>
      )}

      <div className="mb-4">
        <InputBusqueda
          value={busqueda}
          onChange={setBusqueda}
          placeholder="Buscar por nº, aparato, cliente o nº de serie…"
        />
      </div>

      {vista === "tablero" ? (
        lista && (
          <TableroKanban
            ordenes={filtrada}
            onMover={cambiarEstado}
            onEditar={setOrdenForm}
            onFacturar={facturar}
            onRecepcion={setRecepcionOS}
            onBorrar={borrar}
          />
        )
      ) : (
      <div className="panel px-3.5 py-2">
        {!lista ? null : filtrada.length === 0 ? (
          <EstadoVacio
            titulo={q ? `Sin resultados para «${q}»` : "Sin órdenes de servicio"}
            descripcion="La recepción rápida crea la orden y da de alta el aparato en un paso."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Aparato</th>
                  <th>Cliente</th>
                  <th>Avería</th>
                  <th>Entrada</th>
                  <th>Estado</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrada.map((o) => (
                  <tr key={o._id}>
                    <td className="font-bold text-white whitespace-nowrap num">
                      {o.numero}
                      <BadgePresupuesto numero={o.presupuestoNumero} />
                      <BadgeDomicilio tipoServicio={o.tipoServicio} />
                    </td>
                    <td className="text-slate-300">
                      {o.aparatoDescripcion ?? "—"}
                      {o.aparato?.numeroSerie && (
                        <p className="text-[0.6875rem] text-slate-500 num">S/N {o.aparato.numeroSerie}</p>
                      )}
                    </td>
                    <td className="text-slate-300">{o.clienteNombre ?? "—"}</td>
                    <td className="text-slate-400 max-w-[220px] truncate" title={o.averia ?? ""}>{o.averia || "—"}</td>
                    <td className="text-slate-400 whitespace-nowrap num">{fecha(o.fechaEntrada)}</td>
                    <td>
                      <select
                        value={o.estado}
                        onChange={(e) => cambiarEstado(o, e.target.value)}
                        className={`cursor-pointer appearance-none rounded-full border text-[0.6875rem] font-semibold pl-3 pr-7 py-1 bg-no-repeat focus:outline-none ${
                          CLASES_PILL_ESTADO[tonoEstado(o.estado)] ?? CLASES_PILL_ESTADO.slate
                        }`}
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                          backgroundPosition: "right 9px center",
                        }}
                      >
                        {ESTADOS_OS.map((e2) => (
                          <option key={e2.clave} value={e2.clave}>{e2.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-right text-slate-300 whitespace-nowrap num">{euros(o.total)}</td>
                    <td className="text-right whitespace-nowrap">
                      <MenuImprimirOrdenServicio orden={o} />
                      <button
                        onClick={() => setRecepcionOS(o)}
                        title="Recepción digital (fotos + firma)"
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors align-middle mx-1 ${
                          o.recepcionDigital?.firma?.fecha
                            ? "text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                            : "text-slate-400 hover:text-accent hover:bg-accent/10"
                        }`}
                      >
                        <IconCamara />
                      </button>
                      <button onClick={() => setOrdenForm(o)} className="text-xs text-accent hover:underline mr-3 ml-1">
                        Editar
                      </button>
                      {o.factura ? (
                        <Link to="/ventas" title="Factura creada: valídala y emítela desde Ventas" className="inline-block align-middle mr-3">
                          <Badge tono="green">{o.numeroFactura ?? "borrador"}</Badge>
                        </Link>
                      ) : (
                        ["finalizado", "entregado"].includes(o.estado) && (
                          <button onClick={() => facturar(o)} className="text-xs text-amber-300 hover:underline mr-3">
                            Facturar
                          </button>
                        )
                      )}
                      <button onClick={() => borrar(o)} className="text-xs text-rose-400 hover:underline">
                        Borrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {recepcion && (
        <RecepcionRapidaServicio
          onCerrar={() => setRecepcion(false)}
          onCreada={(datos) => {
            setRecepcion(false);
            cargar();
            // Al terminar el alta se abre la recepción digital de la nueva OS
            // para fotos del estado, firma del cliente e impresión de la hoja.
            if (datos?.orden) {
              setRecepcionNueva(true);
              setRecepcionOS(datos.orden);
            }
          }}
        />
      )}

      {ordenForm !== undefined && (
        <FormOrdenServicio
          orden={ordenForm}
          onCerrar={() => setOrdenForm(undefined)}
          onGuardada={() => {
            setOrdenForm(undefined);
            cargar();
          }}
        />
      )}

      {recepcionOS !== undefined && (
        <ModalRecepcionServicio
          orden={recepcionOS}
          recienCreada={recepcionNueva}
          onCerrar={() => {
            setRecepcionOS(undefined);
            setRecepcionNueva(false);
          }}
          onGuardada={cargar}
        />
      )}
    </>
  );
}
